import React, { useState } from 'react';
import clsx from 'clsx';

/**
 * Avatar — user / entity image with graceful fallback to initials.
 *
 * Sizes: xs (24), sm (32), md (40), lg (48), xl (64), 2xl (88)
 * Shape: round (default), square
 */
const SIZES = {
    xs:  { box: 'w-6  h-6',  text: 'text-2xs', ring: 'ring-1'   },
    sm:  { box: 'w-8  h-8',  text: 'text-xs',  ring: 'ring-2'   },
    md:  { box: 'w-10 h-10', text: 'text-sm',  ring: 'ring-2'   },
    lg:  { box: 'w-12 h-12', text: 'text-base',ring: 'ring-2'   },
    xl:  { box: 'w-16 h-16', text: 'text-lg',  ring: 'ring-2'   },
    '2xl':{box: 'w-22 h-22', text: 'text-2xl', ring: 'ring-4'   },
};

function initialsOf(name) {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2);
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Avatar({
    src,
    alt,
    name,
    size = 'md',
    shape = 'round',
    className,
    bordered = false,
    status,                // 'online' | 'busy' | 'offline'
    onClick,
}) {
    const [errored, setErrored] = useState(false);
    const sz = SIZES[size] || SIZES.md;
    const radius = shape === 'square' ? 'rounded-lg' : 'rounded-pill';
    const showImage = src && !errored;
    const label = alt || name || '';

    return (
        <span className={clsx('relative inline-flex shrink-0', onClick && 'cursor-pointer', className)} onClick={onClick}>
            {showImage ? (
                <img
                    src={src}
                    alt={label}
                    onError={() => setErrored(true)}
                    className={clsx(
                        'object-cover',
                        sz.box, radius,
                        bordered && 'ring-2 ring-surface',
                    )}
                />
            ) : (
                <span
                    role="img" aria-label={label || 'משתמש'}
                    className={clsx(
                        'inline-flex items-center justify-center font-semibold',
                        'bg-brand-100 text-brand-700',
                        sz.box, sz.text, radius,
                        bordered && 'ring-2 ring-surface',
                    )}
                >
                    {initialsOf(name || label)}
                </span>
            )}
            {status && (
                <span
                    aria-hidden="true"
                    className={clsx(
                        'absolute bottom-0 end-0 block rounded-pill ring-2 ring-surface',
                        size === 'xs' || size === 'sm' ? 'w-2 h-2' : 'w-2.5 h-2.5',
                        status === 'online'  && 'bg-success-500',
                        status === 'busy'    && 'bg-warning-500',
                        status === 'offline' && 'bg-line-strong',
                    )}
                />
            )}
        </span>
    );
}

export function AvatarGroup({ children, max = 4, size = 'md', className }) {
    const kids = React.Children.toArray(children);
    const visible = kids.slice(0, max);
    const overflow = kids.length - visible.length;
    const sz = SIZES[size] || SIZES.md;

    return (
        <div className={clsx('inline-flex items-center -space-x-2 rtl:space-x-reverse', className)}>
            {visible.map((child, i) =>
                React.isValidElement(child)
                    ? React.cloneElement(child, {
                        key: i,
                        size,
                        bordered: true,
                        className: clsx(child.props.className, 'ring-2 ring-surface'),
                    })
                    : child
            )}
            {overflow > 0 && (
                <span className={clsx(
                    'inline-flex items-center justify-center rounded-pill ring-2 ring-surface bg-surface-muted text-ink-muted font-semibold',
                    sz.box, sz.text,
                )}>
                    +{overflow}
                </span>
            )}
        </div>
    );
}

export default Avatar;
